import { noop } from '@sgme/fp';
import type { PublicationItem } from 'components/discover/publications/model';
import { IntlKey } from 'locales';
import { useCallback, useEffect, useState } from 'react';
import { Carousel, CarouselItem } from 'react-bootstrap';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useOpenPublication } from './hooks/use-open-publication';

interface SpCarouselProps {
  id: string;
  titleKey: IntlKey;
  items: readonly PublicationItem[];
  seeMoreLabelId?: IntlKey;
  stayStill?: boolean;
  onSeeMore(): void;

  onTrackOpen: () => void;
}

export function SpCarousel({
  id,
  titleKey,
  items,
  seeMoreLabelId,
  stayStill = false,
  onSeeMore,
  onTrackOpen,
}: SpCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeItem = items[activeIndex];

  function previous() {
    setActiveIndex(current => (current + items.length - 1) % items.length);
  }

  const next = useCallback(() => {
    setActiveIndex(current => (current + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (items.length > 1) {
      const interval = setInterval(() => next(), 5000 + Math.random() * 500);
      return () => clearInterval(interval);
    }
  }, [next, items.length]);

  const onOpenDocument = useOpenPublication(activeItem, onTrackOpen);

  return (
    <CarouselContainer
      id={id}
      activeIndex={activeIndex}
      next={stayStill ? noop : next}
      previous={stayStill ? noop : previous}
      className="card slide overflow-hidden"
      data-e2e={`publication-${id}`}
      indicators={false}
      controls={false}
      interval={3000}
      slide={true}
      pause={'hover'}
      wrap={true}
      data-ride={'carousel'}
    >
      {items.map(({ id, title, publicationDate, imageUrl, documentUrl, videoId }) => (
        <StyledCarouselItem
          interval={1000}
          tag="div"
          key={id}
          className="h-100"
          $backgroundImageUrl={
            imageUrl ?? (videoId !== undefined ? `https://socgen.qumucloud.com/kulu/${videoId}/thumbnail` : undefined)
          }
        >
          <div className="card-img-overlay bg-blur-gradient bg-dark bg-opacity-75" onClick={onOpenDocument}></div>
          <StyledCaption onClick={onOpenDocument} data-e2e="publication-type">
            <div className="d-flex justify-content-between w-100">
              <span className="badge badge-outline-white badge-md badge-dismissible mb-12px bg-blur px-1">
                <FormattedMessage id={titleKey} />
              </span>
              {publicationDate && (
                <span data-e2e="publication-date">
                  <FormattedDate value={new Date(publicationDate)} day="numeric" month="short" year="numeric" />
                </span>
              )}
            </div>
            <a href={documentUrl} className="text-white" target="_blank">
              <h1 className="card-title h3 mb-0" data-e2e="publication-title">
                {title}
              </h1>
            </a>
          </StyledCaption>
        </StyledCarouselItem>
      ))}
      <CarouselNavigation>
        <div className="d-flex flex-grow-1 align-items-center">
          <button
            className="btn btn-flat-white btn-lg btn-icon"
            type="button"
            onClick={previous}
            data-e2e="publication-prev-btn"
          >
            <i className="icon">chevron_left</i>
            <span className="visually-hidden">Previous</span>
          </button>
          <div className="carousel-indicators m-0 gap-1 position-relative">
            {items.map(({ id, title }, index) => (
              <button
                type="button"
                data-bs-target="#carouselIndicators"
                data-bs-slide-to={index}
                key={id}
                aria-label={title}
                title={title}
                className={index === activeIndex ? 'active' : undefined}
                aria-current="true"
                onClick={() => setActiveIndex(index)}
              ></button>
            ))}
          </div>
          <button
            className="btn btn-flat-white btn-lg btn-icon"
            type="button"
            onClick={next}
            data-e2e="publication-next-btn"
          >
            <i className="icon">navigate_next</i>
            <span className="visually-hidden">Next</span>
          </button>
        </div>
        <div className="flex">
          <button
            className="btn btn-flat-white btn-lg btn-icon-end"
            type="button"
            onClick={onSeeMore}
            data-e2e="publication-seeMore-btn"
          >
            <FormattedMessage id={seeMoreLabelId} defaultMessage="See more" />
            <i className="icon">arrow_forward</i>
          </button>
        </div>
      </CarouselNavigation>
    </CarouselContainer>
  );
}

const StyledCaption = styled.div.attrs({
  className: 'carousel-caption text-start d-flex flex-column align-items-start p-4',
})`
  left: 0;
  right: 0;
  bottom: 40px;
`;

const CarouselContainer = styled(Carousel)`
  min-height: 300px;

  .card-text-description {
    max-height: 60px;
    overflow: hidden;
    display: -webkit-box;
    mix-blend-mode: luminosity;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  .bg-blur {
    backdrop-filter: blur(32px);
  }

  .bg-blur-gradient {
    backdrop-filter: blur(32px);
    mix-blend-mode: luminosity;
    -webkit-mask-image: linear-gradient(to top, rgba(0, 0, 0, 1) 10%, rgba(0, 0, 0, 0) 60%);
    mask-image: linear-gradient(to top, rgba(0, 0, 0, 1) 10%, rgba(0, 0, 0, 0) 60%);
  }

  .carousel-indicators [data-bs-target] {
    width: 6px;
    height: 6px;
    border-top: 0px;
    border-bottom: 0px;
    border-radius: 15px;
    transition-property: width;

    &:hover {
      opacity: 1;
      width: 24px;
    }
  }

  .carousel-indicators:not(:has([data-bs-target]:hover)) .active {
    width: 24px;
  }

  .carousel-inner {
    height: 100%;
  }
`;

const StyledCarouselItem = styled(CarouselItem)`
  background-color: var(--bs-royal-blue);
  background-image: ${({ $backgroundImageUrl }) =>
    $backgroundImageUrl !== undefined ? `url(${$backgroundImageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  cursor: pointer;
`;

const CarouselNavigation = styled.div.attrs({
  className: 'carousel-navigation flex-x w-100 border-top border-light border-opacity-20 position-absolute',
})`
  bottom: 0;
  mix-blend-mode: 'luminosity';
`;
